import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SectionHeader } from "./SectionHeader";
import { useT } from "@/lib/i18n";

export const CottageGrid = () => {
  const { t } = useT();
  return (
    <section id="ubytovanie" className="section-y" aria-labelledby="ubytovanie-title">
      <div className="container-x">
        <SectionHeader kicker={t.intro.kicker} title={t.intro.title} subtitle={t.intro.p1} />
        <p className="max-w-2xl mt-4 text-base text-muted-foreground leading-relaxed">{t.intro.p2}</p>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="cottages-grid">
          {t.cottages.map((c, idx) => (
            <motion.article
              key={c.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: idx * 0.08 }}
              className="group rounded-2xl border border-border bg-card shadow-soft overflow-hidden hover:-translate-y-0.5 hover:shadow-lifted hover:border-foreground/10 transition-[box-shadow,transform,border-color] duration-200 flex flex-col"
              data-testid={`cottage-card-${c.slug}`}
            >
              <div className="relative aspect-[16/11] overflow-hidden bg-muted">
                <img
                  src={c.image}
                  alt={`${c.name}, ${c.tagline}, Chata Ižipovce, Liptov`}
                  loading="lazy"
                  width="880"
                  height="605"
                  className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.05]"
                />
                <div className="absolute left-4 top-4 rounded-full bg-white/90 backdrop-blur px-3 py-1 text-xs font-medium text-foreground shadow-soft">
                  {c.priceFrom}
                </div>
              </div>

              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-display text-2xl font-semibold tracking-tight text-foreground">{c.name}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{c.tagline}</p>
                <p className="mt-4 text-sm leading-relaxed text-foreground/80">{c.description}</p>

                <div className="mt-5 flex flex-wrap gap-1.5">
                  {c.chips.map((chip) => (
                    <Badge key={chip} variant="secondary" className="rounded-full border border-border/70 bg-secondary text-secondary-foreground font-normal">
                      {chip}
                    </Badge>
                  ))}
                </div>

                <div className="mt-6 pt-4 border-t border-border/70 flex items-center justify-between gap-3">
                  <span className="font-display text-base font-semibold text-foreground whitespace-nowrap">{c.priceFrom}</span>
                  <Button asChild size="sm" className="h-10 rounded-xl px-4 whitespace-nowrap" data-testid={`cottage-card-cta-${c.slug}`}>
                    <a href="#kontakt" className="inline-flex items-center gap-1.5">
                      {t.ui.reserve}
                      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </a>
                  </Button>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
